import { useEffect, useState } from 'react'
import { ActionType } from 'klinecharts'
import type { Chart, Crosshair, KLineData } from 'klinecharts'
import { ChevronDown, ChevronUp, Eye, EyeOff, Settings2, Trash2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { DEFAULT_LINE_COLORS } from './indicators'
import { getIndicator } from './types'
import type { IndicatorInstance, ToolIcon } from './types'

// TradingView-style status line + main-pane indicator legend, top-left over the
// candle pane. Replaces klinecharts' default OHLC tooltip (disabled at chart
// creation). Row 1: O H L C  +chg (+chg%) — the market identity (symbol,
// description, timeframe) lives in the top toolbar, not here. Below it: a Vol
// row plus one legend row per main-pane indicator — values coloured to match
// the on-chart lines — collapsible to a count. Everything follows the
// crosshair; the last bar is the fallback readout. Rows wrap at narrow widths,
// and a click/tap pins a row's controls open (touch has no hover).

export interface WorkbenchStatusLineProps {
  chart: Chart
  indicators: IndicatorInstance[]
  onToggleVisible: (id: string) => void
  onOpenSettings: (id: string) => void
  onRemove: (id: string) => void
  volumePresent: boolean
  volumeVisible: boolean
  onToggleVolume: () => void
  onOpenVolumeSettings: () => void
  onRemoveVolume: () => void
}

function fmt(v: number | null | undefined, p: number): string {
  return v == null ? '–' : v.toFixed(p)
}

/** Volume readout (TV "Vol" row), formatted compactly. */
function fmtVol(v: number | undefined): string {
  if (v == null || v === 0) return 'N/A'
  if (v >= 1e9) return (v / 1e9).toFixed(2) + 'B'
  if (v >= 1e6) return (v / 1e6).toFixed(2) + 'M'
  if (v >= 1e3) return (v / 1e3).toFixed(2) + 'k'
  return String(Math.round(v))
}

function LegendBtn({ icon: Icon, title, onClick }: { icon: ToolIcon; title: string; onClick: () => void }) {
  return (
    <button
      type="button"
      title={title}
      onClick={(e) => {
        e.stopPropagation() // don't toggle the row's pinned state
        onClick()
      }}
      className="inline-flex h-4 w-4 items-center justify-center rounded text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
    >
      <Icon size={12} strokeWidth={1.75} />
    </button>
  )
}

interface LegendFig {
  color: string
  value: number
}

interface LegendRow {
  id: string
  name: string
  params: string
  figs: LegendFig[]
  visible: boolean
  values: boolean
}

export function WorkbenchStatusLine({
  chart,
  indicators,
  onToggleVisible,
  onOpenSettings,
  onRemove,
  volumePresent,
  volumeVisible,
  onToggleVolume,
  onOpenVolumeSettings,
  onRemoveVolume,
}: WorkbenchStatusLineProps) {
  const [hoverIndex, setHoverIndex] = useState<number | null>(null)
  const [, setNonce] = useState(0) // bumped when new data is applied → re-read list
  const [collapsed, setCollapsed] = useState(false)
  // Row whose controls are pinned open by click/tap ('vol' or an indicator id).
  // Hover still reveals on desktop; the pin is the touch equivalent.
  const [pinned, setPinned] = useState<string | null>(null)

  useEffect(() => {
    const onData = () => {
      setHoverIndex(null)
      setNonce((n) => n + 1)
    }
    const onCross = (c?: Crosshair) => {
      setHoverIndex(c?.kLineData ? (c.dataIndex ?? null) : null)
    }
    chart.subscribeAction(ActionType.OnDataReady, onData)
    chart.subscribeAction(ActionType.OnCrosshairChange, onCross)
    return () => {
      chart.unsubscribeAction(ActionType.OnDataReady, onData)
      chart.unsubscribeAction(ActionType.OnCrosshairChange, onCross)
    }
  }, [chart])

  const list = chart.getDataList()
  const idx = hoverIndex != null && list[hoverIndex] ? hoverIndex : list.length - 1
  const bar: KLineData | undefined = list[idx]
  const prev: KLineData | undefined = list[idx - 1]
  const p = chart.getPriceVolumePrecision().price

  const up = bar ? bar.close >= bar.open : true
  const dirClass = up ? 'text-success' : 'text-danger'
  const base = prev?.close ?? bar?.open ?? 0
  const chg = bar ? bar.close - base : 0
  const pct = base ? (chg / base) * 100 : 0
  const chgClass = chg >= 0 ? 'text-success' : 'text-danger'
  const sign = (v: number) => (v >= 0 ? '+' : '')
  const volDisplay = bar ? fmtVol(bar.volume) : 'N/A'

  // Main-pane indicator legends (overlays on the candle pane). One legend row
  // per instance; values are read from the live indicator result at the
  // crosshair index and coloured like the on-chart lines.
  const legends: LegendRow[] = indicators
    .filter((i) => i.paneId === 'candle_pane')
    .flatMap((inst) => {
      const ind = getIndicator(chart, 'candle_pane', inst.engineName)
      if (!ind) return []
      const row = (ind.result?.[idx] ?? {}) as Record<string, unknown>
      const cols = inst.settings.colors.length > 0 ? inst.settings.colors : DEFAULT_LINE_COLORS
      const figs: LegendFig[] = []
      ind.figures.forEach((f, i) => {
        const value = row[f.key]
        if (typeof value === 'number') {
          figs.push({ color: inst.settings.textColor ?? cols[i % cols.length], value })
        }
      })
      return [
        {
          id: inst.id,
          name: inst.name,
          params: inst.settings.params.join(','),
          figs,
          visible: inst.settings.visible !== false,
          values: inst.settings.valuesInLegend !== false,
        },
      ]
    })

  // Pin a row's controls open on click/tap (touch has no hover). Button
  // clicks inside the row stopPropagation; pointerup does too, so the chart
  // wrapper's tap-synthesis never routes a legend tap as a chart click.
  const togglePin = (key: string) => setPinned((cur) => (cur === key ? null : key))
  const rowShell = (key: string) =>
    cn(
      'group flex flex-wrap items-center gap-x-1.5 gap-y-0.5 w-fit pointer-events-auto rounded-md -mx-1.5 px-1.5 -my-0.5 py-0.5 transition-colors hover:bg-popover/80 hover:backdrop-blur-sm cursor-pointer',
      pinned === key && 'bg-popover/80 backdrop-blur-sm',
    )
  const reveal = (key: string) =>
    pinned === key
      ? 'opacity-100 pointer-events-auto transition-opacity'
      : 'opacity-0 pointer-events-none transition-opacity group-hover:opacity-100 group-hover:pointer-events-auto'

  return (
    <div className="absolute top-2 left-3 right-3 z-10 flex flex-col items-start gap-1.5 text-label tabular-nums select-none pointer-events-none">
      {/* row 1 — OHLC + change (market identity lives in the toolbar) */}
      {bar && (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
          <span className="flex items-center gap-1">
            <span className="text-muted-foreground">O</span>
            <span className={dirClass}>{fmt(bar.open, p)}</span>
          </span>
          <span className="flex items-center gap-1">
            <span className="text-muted-foreground">H</span>
            <span className={dirClass}>{fmt(bar.high, p)}</span>
          </span>
          <span className="flex items-center gap-1">
            <span className="text-muted-foreground">L</span>
            <span className={dirClass}>{fmt(bar.low, p)}</span>
          </span>
          <span className="flex items-center gap-1">
            <span className="text-muted-foreground">C</span>
            <span className={dirClass}>{fmt(bar.close, p)}</span>
          </span>
          <span className={chgClass}>
            {sign(chg)}
            {fmt(chg, p)} ({sign(pct)}
            {fmt(pct, 2)}%)
          </span>
        </div>
      )}

      {/* main-pane legend — Vol row + one row per overlay indicator; collapses to
          a count. Vol counts as a legend item (TV behaviour). */}
      {bar && (
        <div className="flex flex-col gap-1.5">
          {collapsed ? (
            <button
              type="button"
              onClick={() => setCollapsed(false)}
              className="pointer-events-auto inline-flex w-fit items-center gap-1 px-1.5 py-0.5 rounded-sm bg-muted border border-border text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
            >
              <ChevronDown size={13} strokeWidth={2} />
              <span>{legends.length + (volumePresent ? 1 : 0)}</span>
            </button>
          ) : (
            <>
              {volumePresent && (
                <div
                  className={rowShell('vol')}
                  onClick={() => togglePin('vol')}
                  onPointerUp={(e) => e.stopPropagation()}
                >
                  <span className={volumeVisible ? 'text-muted-foreground' : 'text-muted-foreground/50'}>
                    Vol
                  </span>
                  {volumeVisible && (
                    <span className={volDisplay === 'N/A' ? 'text-muted-foreground' : dirClass}>
                      {volDisplay}
                    </span>
                  )}
                  <span className="inline-flex items-center gap-0.5 ml-0.5">
                    {/* eye stays visible while hidden (to unhide) */}
                    <span className={cn(volumeVisible ? reveal('vol') : 'opacity-100')}>
                      <LegendBtn
                        icon={volumeVisible ? Eye : EyeOff}
                        title={volumeVisible ? 'Hide' : 'Show'}
                        onClick={onToggleVolume}
                      />
                    </span>
                    <span className={cn('inline-flex items-center gap-0.5', reveal('vol'))}>
                      <LegendBtn icon={Settings2} title="Settings" onClick={onOpenVolumeSettings} />
                      <LegendBtn icon={Trash2} title="Remove" onClick={onRemoveVolume} />
                    </span>
                  </span>
                </div>
              )}
              {legends.map((lg) => (
                <div
                  key={lg.id}
                  className={rowShell(lg.id)}
                  onClick={() => togglePin(lg.id)}
                  onPointerUp={(e) => e.stopPropagation()}
                >
                  <span className={lg.visible ? 'text-foreground' : 'text-muted-foreground/50'}>
                    {lg.name}
                  </span>
                  {lg.params && (
                    <span
                      className={cn(
                        'text-caption',
                        lg.visible ? 'text-muted-foreground/60' : 'text-muted-foreground/40',
                      )}
                    >
                      ({lg.params})
                    </span>
                  )}
                  {lg.visible &&
                    lg.values &&
                    lg.figs.map((f, i) => (
                      <span key={i} style={{ color: f.color }}>
                        {fmt(f.value, p)}
                      </span>
                    ))}
                  {/* controls: hover-revealed or click-pinned; when hidden the eye stays visible */}
                  <span className="inline-flex items-center gap-0.5 ml-0.5">
                    <span className={cn(lg.visible ? reveal(lg.id) : 'opacity-100')}>
                      <LegendBtn
                        icon={lg.visible ? Eye : EyeOff}
                        title={lg.visible ? 'Hide' : 'Show'}
                        onClick={() => onToggleVisible(lg.id)}
                      />
                    </span>
                    <span className={cn('inline-flex items-center gap-0.5', reveal(lg.id))}>
                      <LegendBtn icon={Settings2} title="Settings" onClick={() => onOpenSettings(lg.id)} />
                      <LegendBtn icon={Trash2} title="Remove" onClick={() => onRemove(lg.id)} />
                    </span>
                  </span>
                </div>
              ))}
              <button
                type="button"
                onClick={() => setCollapsed(true)}
                className="pointer-events-auto inline-flex w-fit items-center px-1.5 py-0.5 rounded-sm bg-muted border border-border text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
              >
                <ChevronUp size={13} strokeWidth={2} />
              </button>
            </>
          )}
        </div>
      )}
    </div>
  )
}
